/**
 * Workspace Selector - YUL ON TAP
 * 
 * Allows users to choose between workspaces they have access to.
 * Header reflects current workspace selection, not industry.
 * "Switch Workspace" is always available if user has multiple workspaces.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';
import { Building2, UtensilsCrossed, ChevronRight, LogOut } from 'lucide-react';

const WORKSPACE_ICONS = {
  restaurant: UtensilsCrossed,
  hotel: Building2,
};

const WORKSPACE_COLORS = {
  restaurant: '#f97316',
  hotel: '#3b82f6',
};

export default function SelectVertical() {
  const { t, i18n } = useTranslation();
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { 
    user, 
    logout, 
    selectWorkspace, 
    hasWorkspaceAccess, 
    getWorkspaceRoute,
    currentWorkspace,
    industryPortal
  } = useAuth();
  const navigate = useNavigate();

  // Get display name for industry portal
  const getPortalDisplayName = () => {
    const portal = industryPortal || localStorage.getItem('yul_industry_portal');
    const names = {
      restaurant: { en: 'Restaurants', fr: 'Restaurants' },
      automotive: { en: 'Automotive', fr: 'Automobile' },
      healthcare: { en: 'Healthcare', fr: 'Santé' },
      hotel: { en: 'Hotels', fr: 'Hôtels' },
    };
    return names[portal]?.[i18n.language] || names[portal]?.en || portal;
  };

  useEffect(() => {
    const fetchVerticals = async () => {
      try {
        const response = await dashboardAPI.getVerticals();
        // Filter to show ONLY workspaces the user has access to (respects industry portal)
        const allVerticals = response.data.verticals;
        const accessibleVerticals = allVerticals.filter(v => hasWorkspaceAccess(v.id));
        setVerticals(accessibleVerticals);
      } catch (error) {
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchVerticals();
  }, [t, hasWorkspaceAccess]);

  const handleSelectWorkspace = (vertical) => {
    if (!hasWorkspaceAccess(vertical.id)) {
      toast.error(t('common.error'));
      return;
    }
    
    // Select workspace and navigate
    selectWorkspace(vertical.id);
    const route = getWorkspaceRoute(vertical.id);
    navigate(route);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get localized workspace names
  const getWorkspaceName = (id) => {
    const names = {
      restaurant: { en: 'Restaurants', fr: 'Restaurants' },
      hotel: { en: 'Hotels', fr: 'Hôtels' },
    };
    return names[id]?.[i18n.language] || t(`verticals.${id}`);
  };

  const getWorkspaceDescription = (id) => {
    const descs = {
      restaurant: { 
        en: 'Manage restaurants and table service',
        fr: 'Gérer les restaurants et le service aux tables'
      },
      hotel: { 
        en: 'Manage hotel properties and guest engagement',
        fr: 'Gérer les propriétés hôtelières et l\'engagement des clients'
      },
    };
    return descs[id]?.[i18n.language] || t(`verticals.${id}Description`);
  };

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] flex flex-col">
      {/* Header */}
      <header className="border-b border-[hsl(217,19%,27%)] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-semibold text-white">YUL ON TAP</span>
              <p className="text-xs text-[hsl(215,20%,65%)]">
                {getPortalDisplayName()} Portal
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSelector variant="compact" />
            <span className="text-sm text-[hsl(215,20%,65%)]">{user?.name || user?.email}</span>
            <Button
              variant="ghost"
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-[hsl(215,20%,65%)] hover:text-white hover:bg-[hsl(217,19%,17%)]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-semibold text-white tracking-tight">
              {i18n.language === 'fr' ? 'Sélectionner l\'espace de travail' : 'Select Workspace'}
            </h1>
            <p className="text-[hsl(215,20%,65%)]">
              {i18n.language === 'fr' 
                ? 'Choisissez un espace de travail à gérer'
                : 'Choose a workspace to manage'
              }
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="h-48 rounded-lg bg-[hsl(217,19%,12%)] animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {verticals.map((vertical) => {
                const Icon = WORKSPACE_ICONS[vertical.id] || Building2;
                const color = WORKSPACE_COLORS[vertical.id] || '#3b82f6';
                const isCurrentWorkspace = currentWorkspace === vertical.id;
                
                return (
                  <Card
                    key={vertical.id}
                    data-testid={`workspace-card-${vertical.id}`}
                    className={`bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] cursor-pointer transition-all duration-200 hover:bg-[hsl(217,19%,15%)] group ${
                      isCurrentWorkspace ? 'border-blue-500' : 'hover:border-blue-500/50'
                    }`}
                    onClick={() => handleSelectWorkspace(vertical)}
                  >
                    <CardHeader className="pb-2">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <CardTitle className="text-xl text-white flex items-center justify-between">
                        {getWorkspaceName(vertical.id)}
                        <ChevronRight className="w-5 h-5 text-[hsl(215,20%,45%)] group-hover:text-white transition-colors" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-[hsl(215,20%,65%)]">
                        {getWorkspaceDescription(vertical.id)}
                      </CardDescription>
                      {isCurrentWorkspace && (
                        <p className="text-xs text-blue-400 mt-2">
                          {i18n.language === 'fr' ? 'Espace actuel' : 'Current workspace'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
